import { BubbleChatOutcomeIcon, Delete02Icon, UserSwitchIcon } from '@hugeicons/core-free-icons';
import cn from 'classnames';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';

import { FlagSeverityCounts } from 'components/FlagSeverityCounts/FlagSeverityCounts';
import { IconComponent } from 'components/Icon/IconComponent';
import StatusPill from 'components/Pills/StatusPills';

import styles from './RefereeCard.module.scss';

export const RefereeCard = ({
  title = 'Referee',
  name,
  subtitle,
  referee,
  referees = [],
  selected,
  disabled,
  combined = false,
  flagCounts,
  onClick = () => {},
  onDelete,
}) => {
  const { t } = useTranslation();
  const ipMatches = referee.warning_flag && referee.status === 'completed';

  return (
    <div
      data-testid={`referee-card-${referee.id}`}
      data-status={referee.status}
      className={cn(
        'border-1 border rounded-3xl shadow-sm bg-neutral-1 overflow-hidden',
        selected ? 'border-primary-4' : 'border-primary-1',
        { [styles.rootDisabled]: disabled },
      )}
      onClick={() => !disabled && onClick(referee)}>
      <div className="d-flex flex-column u-width-100">
        {referee.replaced_by && (
          <div
            className={cn(
              'border-1 border border-primary-1 rounded-t-3xl bg-blue-1 p-2 flex items-center justify-center',
              { [styles.disabledContent]: disabled },
            )}>
            <IconComponent icon={UserSwitchIcon} noBackground size="small" type="neutral-light" />
            <span className="u-font-size-small u-font-weight-400">
              {(() => {
                const replacedReferee = referees?.find(r => r.id === referee.replaced_by);
                return replacedReferee?.name?.trim()
                  ? `Replaced by ${replacedReferee.name}`
                  : 'Awaiting replacement';
              })()}
            </span>
          </div>
        )}
        {referee.answered_on_behalf_user_id && !combined && (
          <OverlayTrigger
            placement="top"
            overlay={<Tooltip>Answered on behalf by {referee.answered_on_behalf_user}</Tooltip>}>
            <div
              data-testid="referee-on-behalf-badge"
              className={cn('rounded-t-2xl bg-primary-1 p-2 flex items-center justify-center', {
                [styles.disabledContent]: disabled,
              })}>
              <IconComponent
                icon={BubbleChatOutcomeIcon}
                noBackground
                size="small"
                type="neutral-light"
              />
              <span className="text-xl font-medium">Answered on behalf of Candidate</span>
            </div>
          </OverlayTrigger>
        )}
        <div
          className={cn(
            styles.details,
            'd-flex justify-content-between u-width-100 align-items-start u-padding',
          )}>
          <div
            className={cn('d-flex flex-column u-gap', styles.content, {
              [styles.disabledContent]: disabled,
            })}>
            <div className="d-flex flex-column">
              <div className="u-font-size-x-small u-font-weight-400">{title}</div>
              <div
                className={cn(
                  'u-font-size-x-large u-font-weight-500 primary-text leading-none display-font',
                  styles.name,
                )}>
                {name}
              </div>
            </div>
            {!combined && ipMatches && (
              <div className="flex items-center gap-4">
                <StatusPill
                  status="warning"
                  text={t('flagSeverity.ipMatch')}
                  className="text-uppercase"
                  small
                  noBorder
                  colorDot="bg-orange-2"
                  lightBackground
                  dataTestId="referee-ip-match-pill"
                />
              </div>
            )}
            {subtitle && (
              <div className="u-font-size-x-small u-font-weight-400 secondary-text">{subtitle}</div>
            )}
            {!combined && <FlagSeverityCounts counts={flagCounts} />}
          </div>
          <div className={styles.side}>
            {!combined && (
              <StatusPill
                status={referee.status === 'candidate' ? 'pending' : referee.status}
                text={!disabled ? referee.status : ''}
                className={cn('u-uppercase', styles.statusPill, {
                  [styles.disabledContent]: disabled,
                })}
                small
                dataTestId="referee-status-pill"
              />
            )}
            {onDelete && (
              <IconComponent
                icon={Delete02Icon}
                type="error"
                noBackground
                border={false}
                size="small"
                tip="Delete pending nomination"
                className="cursor-pointer"
                onClick={event => {
                  event.stopPropagation();
                  onDelete(referee);
                }}
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
